import classNames from "classnames";
import React, { InputHTMLAttributes } from "react";
import { FieldError, RegisterOptions } from "react-hook-form";
import { useIntl } from "react-intl";

function useInputErrorMessages(): Partial<
  Record<keyof RegisterOptions, string | null>
> {
  const intl = useIntl();
  return {
    required: intl.formatMessage({
      defaultMessage: "This field is required.",
      id: "6B5Jtu",
    }),
    minLength: intl.formatMessage({
      defaultMessage: "This field should be longer.",
      id: "JVKs1i",
    }),
    maxLength: intl.formatMessage({
      defaultMessage: "This field should be shorter.",
      id: "7EpQXl",
    }),
    min: intl.formatMessage({
      defaultMessage: "This field must be larger.",
      id: "g5De5P",
    }),
    max: intl.formatMessage({
      defaultMessage: "This field must be smaller.",
      id: "QcKhR8",
    }),
    pattern: intl.formatMessage({
      defaultMessage: "This field is invalid.",
      id: "A2p8Pq",
    }),
    validate: intl.formatMessage({
      defaultMessage: "This field is invalid.",
      id: "A2p8Pq",
    }),
    valueAsDate: null,
    valueAsNumber: null,
    setValueAs: null,
  };
}

const getErrorMessage = (
  defaultMessages: Partial<Record<keyof RegisterOptions, string | null>>,
  type: FieldError["type"]
) => {
  if (type in defaultMessages) {
    return defaultMessages[type as keyof RegisterOptions];
  }
  return defaultMessages.pattern;
};

interface CustomProps {
  boxSize?: "sm" | "md" | "lg";
  bg?: "light" | "dark" | "transparent";
  label?: React.ReactNode;
  before?: React.ReactNode;
  after?: React.ReactNode;
  error?: FieldError;
  beforeClassName?: string;
  inputClassname?: string;
  wrapperClassname?: string;
  labelClassname?: string;
}

export type TextInputProps = CustomProps &
  InputHTMLAttributes<HTMLInputElement>;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (props, ref) => {
    const {
      before,
      after,
      beforeClassName,
      label,
      boxSize = "md",
      bg = "transparent",
      error,
      inputClassname,
      wrapperClassname,
      labelClassname,
      ...restProps
    } = props;
    const messages = useInputErrorMessages();
    const beforeClasses = classNames("input-before", beforeClassName);
    const classes = classNames(
      "input",
      boxSize !== "md" && `input-${boxSize}`,
      bg !== "transparent" && `input-${bg}`,
      inputClassname
    );
    return (
      <div className={classNames("inputBlock", wrapperClassname)}>
        {label && <label className={labelClassname}>{label}</label>}
        <div className={classes}>
          {before && <div className={beforeClasses}>{before}</div>}
          <input
            {...restProps}
            ref={ref}
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        {after && <div>{after}</div>}
        {error && (
          <small className="text-danger">
            {getErrorMessage(messages, error.type)}
          </small>
        )}
      </div>
    );
  }
);

export type TextareaInputProps = CustomProps &
  InputHTMLAttributes<HTMLTextAreaElement> & {
    cols?: number;
    rows?: number;
    resize?: "none" | "vertical";
  };

export const TextareaInput = React.forwardRef<
  HTMLTextAreaElement,
  TextareaInputProps
>((props, ref) => {
  const {
    before,
    beforeClassName,
    wrapperClassname,
    labelClassname,
    label,
    boxSize = "md",
    bg = "transparent",
    error,
    resize = "vertical",
    ...restProps
  } = props;
  const messages = useInputErrorMessages();
  const beforeClasses = classNames("input-before", beforeClassName);
  const classes = classNames(
    "input",
    boxSize !== "md" && `input-${boxSize}`,
    bg !== "transparent" && `input-${bg}`
  );
  return (
    <div className={classNames("inputBlock", wrapperClassname)}>
      {label && <label className={labelClassname}>{label}</label>}
      <div className={classes}>
        {before && <div className={beforeClasses}>{before}</div>}
        <textarea {...restProps} ref={ref} style={{ resize }} />
      </div>
      {error && (
        <small className="text-danger">
          {getErrorMessage(messages, error.type)}
        </small>
      )}
    </div>
  );
});

export type OptionInputProps = CustomProps &
  InputHTMLAttributes<HTMLSelectElement>;

export const OptionInput = React.forwardRef<
  HTMLSelectElement,
  OptionInputProps
>((props, ref) => {
  const {
    before,
    beforeClassName,
    boxSize = "md",
    bg = "transparent",
    label,
    error,
    wrapperClassname,
    labelClassname,
    ...restProps
  } = props;
  const messages = useInputErrorMessages();
  const beforeClasses = classNames("input-before", beforeClassName);
  const classes = classNames(
    "input",
    boxSize !== "md" && `input-${boxSize}`,
    bg !== "transparent" && `input-${bg}`
  );
  return (
    <div className={classNames("inputBlock", wrapperClassname)}>
      {label && <label className={labelClassname}>{label}</label>}
      <div className={classes}>
        {before && <div className={beforeClasses}>{before}</div>}
        <select {...restProps} ref={ref} />
      </div>
      {error && (
        <small className="text-danger">
          {getErrorMessage(messages, error.type)}
        </small>
      )}
    </div>
  );
});
